import { useSelector } from 'react-redux';
import { Card, Grid, IconName, Stack, StackItem, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { SecurityOverviewResponse } from '@mews-commander/data-access';
import { LocalizedPercentage, lt } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { isCurrentEnterprisePortfolio } from '@mews-commander/legacy-entities';
import { SecurityDataTestId } from '../utils/SecurityDataTestId';
import { useSecurityDashboardFlags } from './useSecurityDashboardFlags';

export const SecurityOverview = ({ data }: { data: SecurityOverviewResponse }) => {
    const { enabledSecurityFeatures, availableSecurityFeatures } = useSecurityFeatures(data);
    const isPortfolio = useSelector(isCurrentEnterprisePortfolio);
    const showTooManyAdminsWarning = data.adminPercentage >= 0.1 && data.adminCount > 2;

    return (
        <Grid templateColumns="1fr 1fr" gap="200" largePhoneMax={{ templateColumns: '1fr' }}>
            <CardAndStack data-test-id={SecurityDataTestId.UserAccountsSecured}>
                <StackItem>
                    <Typography textStyle="highlightMedium" as="div">
                        <LocalizedPercentage value={data.securedAccountsPercentage || 0} />
                    </Typography>
                    {lt('SecurityOverviewStatsSecuredAccounts')}
                </StackItem>
                <TooltippedIcon
                    value={IconName.Info}
                    size="small"
                    tooltip={lt('SecurityOverviewStatsSecuredAccountsTooltip')}
                />
            </CardAndStack>
            <CardAndStack data-test-id={SecurityDataTestId.UsersLoggedInWithPasskeys}>
                <StackItem>
                    <Typography textStyle="highlightMedium" as="div">
                        <LocalizedPercentage value={data.passkeyUsagePercentage || 0} />
                    </Typography>
                    {lt('SecurityOverviewStatsPasskeyLogins')}
                </StackItem>
            </CardAndStack>
            <CardAndStack data-test-id={SecurityDataTestId.UsersWithAdminAccess}>
                <StackItem>
                    <Typography textStyle="highlightMedium" as="div">
                        {data.adminCount}
                        {UnicodeEntity.ForwardSlash}
                        {data.totalProfileCount}
                    </Typography>
                    {lt(isPortfolio ? 'SecurityOverviewStatsAdminUsersMmp' : 'SecurityOverviewStatsAdminUsers')}
                </StackItem>
                {showTooManyAdminsWarning && (
                    <TooltippedIcon
                        value={IconName.Problematic}
                        size="small"
                        tooltip={lt('SecurityOverviewStatsAdminUsersTooltip')}
                        data-test-id={SecurityDataTestId.TooManyAdminUsersTooltip}
                    />
                )}
            </CardAndStack>
            <CardAndStack data-test-id={SecurityDataTestId.SecurityFeaturesActivated}>
                <StackItem>
                    <Typography textStyle="highlightMedium" as="div">
                        {enabledSecurityFeatures.length}
                        {UnicodeEntity.ForwardSlash}
                        {availableSecurityFeatures.length}
                    </Typography>
                    {lt('SecurityOverviewStatsSecurityFeatures')}
                </StackItem>
            </CardAndStack>
        </Grid>
    );
};

const CardAndStack = ({ children, ...other }: React.PropsWithChildren<{ 'data-test-id': SecurityDataTestId }>) => (
    <Card padding="200" {...other}>
        <Stack distribution="space-between" spacing="100" itemAlignment="start">
            {children}
        </Stack>
    </Card>
);

type SecurityFeatureType = 'mfa' | 'deviceAuthorization' | 'sso';

const useSecurityFeatures = (data: SecurityOverviewResponse) => {
    const { hasSsoSubscription } = useSecurityDashboardFlags();

    const availableSecurityFeatures: SecurityFeatureType[] = compactArray([
        'mfa',
        'deviceAuthorization',
        hasSsoSubscription ? 'sso' : null,
    ]);

    const enabledSecurityFeatures: SecurityFeatureType[] = compactArray([
        data.mfaEnforced ? 'mfa' : null,
        data.deviceAuthorizationEnforced ? 'deviceAuthorization' : null,
        availableSecurityFeatures.includes('sso') && data.ssoEnabled ? 'sso' : null,
    ]);

    return {
        availableSecurityFeatures,
        enabledSecurityFeatures,
    };
};
